import type {
  Question,
  WordConnectFinishAudit,
  TileMatchFinishAudit,
  McFinishAudit,
  WitcFinishAudit,
  StopFinishAudit,
  GeniusFinishAudit,
} from '../services/api';
import { normalizePlayDifficultyLabel } from './quizPlayBandScoring';
import { secondsToTimeSpanString } from './pvpAsync';

export type SoloArenaResultLine = {
  pointsEarned: number;
  finalAnswerText?: string | null;
};

/** Payload for async Arena listing after solo (matches `pvpService.completeAsyncHostQuiz`). */
export type SoloArenaPublishSnapshot = {
  quizId: string;
  playDifficulty: string;
  roomDisplayName: string;
  score: number;
  correctAnswers: number;
  totalQuestions: number;
  durationSeconds: number;
  skippedQuestions: number;
  questionIdsInOrder: string[];
  answerIdsPerQuestion: string[][];
  /** Genius PvP: 10-round sequence (panel ids may repeat). */
  geniusSequenceQuestionIds?: string[];
  responseTimeSecondsPerQuestion: number[];
  correctPerQuestion: boolean[];
  wordConnectFinishAudit?: WordConnectFinishAudit;
  wordConnectResults?: Array<{ pointsEarned: number; finalAnswerText?: string | null }>;
  tileMatchFinishAudit?: TileMatchFinishAudit;
  tileMatchResults?: SoloArenaResultLine[];
  mcFinishAudit?: McFinishAudit;
  witcFinishAudit?: WitcFinishAudit;
  witcResults?: SoloArenaResultLine[];
  stopFinishAudit?: StopFinishAudit;
  stopResults?: SoloArenaResultLine[];
  geniusFinishAudit?: GeniusFinishAudit;
  geniusResults?: SoloArenaResultLine[];
};

/** Maps solo Arena snapshot → `pvpService.completeAsyncHostQuiz` body (incl. format finish audits). */
export function buildSoloArenaHostCompleteBody(snap: SoloArenaPublishSnapshot) {
  return {
    score: snap.score,
    correctAnswers: snap.correctAnswers,
    totalQuestions: snap.totalQuestions,
    completionTime: secondsToTimeSpanString(snap.durationSeconds),
    skippedQuestions: snap.skippedQuestions,
    questionIdsInOrder: snap.questionIdsInOrder,
    answerIdsPerQuestion: snap.answerIdsPerQuestion,
    responseTimeSecondsPerQuestion: snap.responseTimeSecondsPerQuestion,
    correctPerQuestion: snap.correctPerQuestion,
    hostPerformanceAlreadyCreditedFromSolo: true,
    geniusSequenceQuestionIds: snap.geniusSequenceQuestionIds,
    wordConnectFinishAudit: snap.wordConnectFinishAudit,
    wordConnectResults: snap.wordConnectResults,
    mcFinishAudit: snap.mcFinishAudit,
    stopFinishAudit: snap.stopFinishAudit,
    stopResults: snap.stopResults,
    tileMatchFinishAudit: snap.tileMatchFinishAudit,
    tileMatchResults: snap.tileMatchResults,
    witcFinishAudit: snap.witcFinishAudit,
    witcResults: snap.witcResults,
    geniusFinishAudit: snap.geniusFinishAudit,
    geniusResults: snap.geniusResults,
  };
}


/** Same correctness bands as MC scoring — must align with PvP host-complete `correctPerQuestion`. */
export function mcCorrectPerQuestionForSoloBand(
  qs: Question[],
  answersForSubmit: Map<number, string>,
  bandLabel: string
): boolean[] {
  const norm = normalizePlayDifficultyLabel(bandLabel);
  return qs.map((q, i) => {
    const ans = answersForSubmit.get(i);
    if (ans === undefined || ans === 'skip') return false;
    const picked = q.answers.find((a) => a.id === ans);
    if (!picked) return false;
    if (norm === 'Easy') return picked.isCorrectEasy ?? picked.isCorrect;
    if (norm === 'Medium') return picked.isCorrectMedium ?? picked.isCorrect;
    return picked.isCorrect;
  });
}
